/**
 * Autofill detector for Koning forms.
 *
 * Chrome/Edge/Safari fire a CSS animation named `koning-autofill-start` the moment
 * they inject autofilled values into a `.koning-input`. We listen for the
 * `animationstart` event at document level, then flag the input with
 * `data-autofilled="true"` AND inject a small gold-check badge as a sibling so
 * the user instantly sees which fields were pre-filled by the browser.
 *
 * A separate `koning-autofill-cancel` animation runs when Chrome clears the
 * autofill (e.g. user retypes), and the badge is removed.
 *
 * No React state needed — pure DOM. Runs once on app mount.
 */
const BADGE_CLASS = "koning-autofill-badge";

function _ensureBadge(input) {
  if (!input || !input.parentElement) return;
  // Skip if a badge is already present as a sibling
  if (input.parentElement.querySelector(`.${BADGE_CLASS}[data-for="${input.dataset.testid || input.name || ""}"]`)) return;
  const badge = document.createElement("span");
  badge.className = BADGE_CLASS;
  badge.setAttribute("aria-label", "Automatisch ingevuld");
  badge.setAttribute("title", "Automatisch ingevuld door je browser");
  badge.setAttribute("data-for", input.dataset.testid || input.name || "");
  // Inline SVG check icon — same gold as CTA (var(--m-orange) via CSS)
  const SVG_NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 16 16");
  svg.setAttribute("width", "12");
  svg.setAttribute("height", "12");
  svg.setAttribute("aria-hidden", "true");
  const pathEl = document.createElementNS(SVG_NS, "path");
  pathEl.setAttribute("fill", "none");
  pathEl.setAttribute("stroke", "currentColor");
  pathEl.setAttribute("stroke-width", "2.4");
  pathEl.setAttribute("stroke-linecap", "round");
  pathEl.setAttribute("stroke-linejoin", "round");
  pathEl.setAttribute("d", "M3.2 8.4l3 3 6.6-6.6");
  svg.appendChild(pathEl);
  badge.appendChild(svg);
  // Ensure the parent can host an absolutely-positioned badge without shifting
  const parent = input.parentElement;
  const style = window.getComputedStyle(parent);
  if (style.position === "static") {
    parent.style.position = "relative";
  }
  parent.appendChild(badge);
}

function _removeBadge(input) {
  if (!input || !input.parentElement) return;
  const key = input.dataset.testid || input.name || "";
  const badge = input.parentElement.querySelector(`.${BADGE_CLASS}[data-for="${key}"]`);
  if (badge) badge.remove();
}

export function initAutofillDetector() {
  if (typeof document === "undefined") return () => {};
  if (document.__koningAutofillInit) return () => {};
  document.__koningAutofillInit = true;

  const onAnimStart = (e) => {
    if (!e || !e.target) return;
    const target = e.target;
    if (!target.matches || !target.matches(".koning-input")) return;
    if (e.animationName === "koning-autofill-start") {
      target.setAttribute("data-autofilled", "true");
      _ensureBadge(target);
    } else if (e.animationName === "koning-autofill-cancel") {
      target.removeAttribute("data-autofilled");
      _removeBadge(target);
    }
  };

  // Also clear the flag when user starts typing manually (so the checkmark
  // doesn't linger after a user overwrites the autofilled value).
  const onInput = (e) => {
    if (!e || !e.target) return;
    const target = e.target;
    if (!target.matches || !target.matches(".koning-input[data-autofilled]")) return;
    // Only remove if the input value no longer matches an autofilled state.
    // We use requestAnimationFrame so Chrome's own autofill cleanup runs first.
    requestAnimationFrame(() => {
      if (target.value === "" || document.activeElement === target) {
        target.removeAttribute("data-autofilled");
        _removeBadge(target);
      }
    });
  };

  document.addEventListener("animationstart", onAnimStart, true);
  document.addEventListener("input", onInput, true);

  return () => {
    document.removeEventListener("animationstart", onAnimStart, true);
    document.removeEventListener("input", onInput, true);
    document.__koningAutofillInit = false;
  };
}
